import React, { useCallback, useContext, useEffect, useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import _ from 'underscore';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import {
  Button,
  Checkbox,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
} from '@fluentui/react-components';
import { Form, FormGroup, Row, Col, Label } from '@bingads-webui-react/primitive';
import { Input, ValidationGroup } from '@bingads-webui-react/primitive-validation';
import {
  useSendOnboardEmail,
  extractCacheRelatedId,
} from '@bingads-webui-campaign-react/pmax-landing-utils';
import { useFloatingBannerApi } from '@bingads-webui-campaign-react/floating-banner-utils';

import { getEmailMeDialogStyles } from '../styles/email-me-dialog-styles';
import { CloseIcon } from './icons';
import EmailMeLogo from '../../media/email-me-logo.png';
import SendSuccessImage from '../../media/send-success.png';
import SendFailureImage from '../../media/send-failure.png';

const userInfoScema = {
  name: 'User Info schema',
  type: 'object',
  properties: {
    firstName: {
      type: 'string',
      maxLength: 100,
      distinguishWideChar: true,
    },
    lastName: {
      type: 'string',
      maxLength: 100,
      distinguishWideChar: true,
    },
    email: {
      required: true,
      type: 'string',
      maxLength: 100,
      pattern: /^([^\s@,;]+@[\w\-.]+\.[A-Za-z]+)$/,
    },
    phone: {
      type: 'string',
      maxLength: 20,
      // eslint-disable-next-line no-useless-escape
      pattern: /^(\+0?\d{1,3})?((\s|\(|\))*\-*\d){8,10}$|^$/,
    },
  },
};

const SEND_EMAIL_ERRORS = {
  NotFoundError: 'NotFound',
};

export const EmailMeDialog = ({ signUpUrlWithCacheId, website, logger }) => {
  const i18n = useContext(I18nContext);
  const classes = useDefaultStyles(getEmailMeDialogStyles);
  const [isOpen, setIsOpen] = useState(false);
  const [isValid, setIsValid] = useState(true);
  const [agreeChecked, setAgreeChecked] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [userInfo, setUserInfo] = useState({});
  const [showSendResult, setShowSendResult] = useState(false);
  const [sendResult, setSendResult] = useState({});

  const { sendOnboardEmail } = useSendOnboardEmail();
  const { showWarningMessage } = useFloatingBannerApi();
  const { cacheId: lookupId } = extractCacheRelatedId(signUpUrlWithCacheId);

  const handleSubmit = useCallback((ev) => {
    ev.preventDefault();
    const formData = new FormData(ev.target).entries();
    const userInfoData = Object.fromEntries(formData);
    logger.trace(`[EmailMeDialog] Form submitted with data: ${JSON.stringify(userInfoData)}`);
    setUserInfo(userInfoData);
    setIsSending(true);
  }, [logger]);

  const handleEmailError = useCallback((errors) => {
    if (_.has(errors, 'pattern')) {
      return [i18n.getString(_TL_('Please enter a valid email address.'))];
    }
    return _.values(errors);
  }, [i18n]);

  const handlePhoneError = useCallback((errors) => {
    if (_.has(errors, 'pattern')) {
      return [i18n.getString(_TL_('Please enter a valid phone number.'))];
    }
    return _.values(errors);
  }, [i18n]);

  const onValidate = useCallback((e) => {
    setIsValid(e.isValid);
  }, []);

  const sendEmailApi = useCallback(
    info => sendOnboardEmail(signUpUrlWithCacheId, info, website),
    [sendOnboardEmail, signUpUrlWithCacheId, website]
  );

  const triggerDialog = useCallback((isDialogOpen) => {
    if (isDialogOpen) {
      logger.trace('[EmailMeDialog] Open Email Me dialog');
    } else {
      logger.trace('[EmailMeDialog] Close Email Me dialog');
    }
    setIsOpen(isDialogOpen);
  }, [logger]);

  const errorProcessor = useCallback((error) => {
    const errorTitle = _.get(error, ['body', 'title']);

    switch (errorTitle) {
      case SEND_EMAIL_ERRORS.NotFoundError:
        showWarningMessage(i18n.getString(_TL_('Campaign recommendations not found, please try to refresh your ads.')));
        break;
      default:
        break;
    }
  }, [i18n, showWarningMessage]);

  useEffect(() => {
    if (isSending) {
      logger.trace('[EmailMeDialog] Sending email');
      sendEmailApi(userInfo)
        .then(() => {
          logger.trace('[EmailMeDialog] Email sent successfully:');
          setSendResult({
            isOK: true,
            title: i18n.getString(_TL_('We\'ve sent your campaign recommendations')),
            text: i18n.getString(_TL_('Please allow a few minutes for the email to arrive.')),
          });
        })
        .catch((err) => {
          logger.trace(`[EmailMeDialog] Email failed to send: ${JSON.stringify(err)}`);
          setSendResult({
            isOK: false,
            title: i18n.getString(_TL_('We\'re unable to send your campaign recommendations')),
            text: i18n.getString(_TL_('We\'re experiencing an issue sending the campaign recommendations. Please verify that your email address is correct and try again.')),
          });
          errorProcessor(err);
        })
        .finally(() => {
          setShowSendResult(true);
          setIsSending(false);
          logger.trace('[EmailMeDialog] Email sending completed');
        });
    }
  }, [errorProcessor, i18n, isSending, logger, sendEmailApi, userInfo]);

  if (!lookupId) {
    return null;
  }

  return (
    <React.Fragment>
      <Button
        className="do-it-later-button"
        onClick={() => triggerDialog(true)}
      >
        {i18n.getString(_TL_('I\'ll do it later'))}
      </Button>
      {isOpen &&
        <Dialog className="email-me-dialog" open>
          <DialogSurface className={classNames('email-me-dialog-surface', classes.dialogSurface)}>
            <DialogBody className={classNames('email-me-dialog-body', classes.dialogBody)}>
              <DialogTitle
                className={classNames('email-me-dialog-title', classes.dialogTitle)}
              >
                <div className={classNames('email-me-title-image-container', classes.titleImageContainer)}>
                  {!showSendResult &&
                    <img src={EmailMeLogo} alt={i18n.getString(_TL_('Email Me'))} />
                  }
                  {showSendResult && sendResult.isOK &&
                    <img src={SendSuccessImage} alt={i18n.getString(_TL_('Send Success'))} />
                  }
                  {showSendResult && !sendResult.isOK &&
                    <img src={SendFailureImage} alt={i18n.getString(_TL_('Send Failure'))} />
                  }
                </div>
                <Button
                  className="close-button"
                  appearance="subtle"
                  aria-label="close"
                  onClick={() => { triggerDialog(false); setSendResult({}); setShowSendResult(false); }}
                  icon={<CloseIcon />}
                />
              </DialogTitle>
              <DialogContent className="email-me-dialog-content">
                {showSendResult ?
                  <div>
                    <div className={classNames('email-me-dialog-content-title', classes.dialogContentTitle)}>
                      <span>{sendResult.title}</span>
                    </div>
                    <div className={classNames('email-me-dialog-content-text', classes.dialogContentText)}>
                      <span>{sendResult.text}</span>
                    </div>
                    <div className={classNames('send-button-container', classes.sendButtonContainer)}>
                      <Button className="action-button" appearance="primary" onClick={() => { setSendResult({}); setShowSendResult(false); triggerDialog(!sendResult.isOK); }}>
                        {sendResult.isOK ? i18n.getString(_TL_('Close')) : i18n.getString(_TL_('Try again'))}
                      </Button>
                    </div>
                  </div>
                  :
                  <div>
                    <div className={classNames('email-me-dialog-content-title', classes.dialogContentTitle)}>
                      <span>{i18n.getString(_TL_('Receive campaign recommendations by email'))}</span>
                    </div>
                    <Form className={classNames('email-me-dialog-form', classes.dialogForm)} onSubmit={handleSubmit}>
                      <ValidationGroup
                        validationProps={{ schema: userInfoScema, data: userInfo, onValidate }}
                      >
                        <FormGroup>
                          <Row>
                            <Col md={6} className="first-name-field">
                              <Label htmlFor="first-name-input">{i18n.getString(_TL_('First Name'))}</Label>
                              <Input className="input-box" type="text" name="firstName" id="first-name-input" validationProps={{ field: 'firstName' }} />
                            </Col>
                            <Col md={6} className="last-name-field">
                              <Label htmlFor="last-name-input">{i18n.getString(_TL_('Last Name'))}</Label>
                              <Input className="input-box" type="text" name="lastName" id="last-name-input" validationProps={{ field: 'lastName' }} />
                            </Col>
                          </Row>
                        </FormGroup>
                        <FormGroup>
                          <Row>
                            <Col md={6} className="phone-field">
                              <Label htmlFor="phone-input">{i18n.getString(_TL_('Phone number'))}</Label>
                              <Input className="input-box" type="tel" name="phone" id="phone-input" validationProps={{ field: 'phone', errorProcessor: handlePhoneError }} />
                            </Col>
                            <Col md={6} className="email-field">
                              <Label required htmlFor="email-input">{i18n.getString(_TL_('Email address'))}</Label>
                              <Input required className="input-box" type="email" name="email" id="email-input" validationProps={{ field: 'email', errorProcessor: handleEmailError }} />
                            </Col>
                          </Row>
                        </FormGroup>
                        <Checkbox
                          className={classNames('agree-checkbox', agreeChecked ? 'checked' : 'unchecked')}
                          checked={agreeChecked}
                          onChange={(ev, data) => setAgreeChecked(data.checked)}
                          label={i18n.getString(_TL_('By submitting, you agree to receive emails and materials from Microsoft Advertising, including promotional offers and newsletters.'))}
                        />
                        <div className={classNames('send-button-container', classes.sendButtonContainer)}>
                          <Button className="action-button" appearance="primary" type="submit" disabled={!agreeChecked || isSending || !isValid}>
                            {isSending ? i18n.getString(_TL_('Submitting')) : i18n.getString(_TL_('Submit'))}
                          </Button>
                        </div>
                      </ValidationGroup>
                    </Form>
                  </div>}
              </DialogContent>
            </DialogBody>
          </DialogSurface>
        </Dialog>}
    </React.Fragment >
  );
};

EmailMeDialog.propTypes = {
  signUpUrlWithCacheId: PropTypes.string.isRequired,
  website: PropTypes.string,
  logger: PropTypes.shape({
    trace: PropTypes.func.isRequired,
  }),
};

EmailMeDialog.defaultProps = {
  website: '',
  logger: {
    trace: _.noop,
  },
};
